import { Lead } from '../types/Lead';

/**
 * Search utility functions for filtering leads
 * Provides case-insensitive, partial matching with performance optimizations
 */

// Constants for search configuration
export const SEARCH_CONFIG = {
  DEFAULT_FIELDS: ['all'] as const,
  DEFAULT_CASE_SENSITIVE: false,
  DEFAULT_EXACT_MATCH: false,
  MIN_QUERY_LENGTH: 1,
  MAX_SUGGESTIONS: 5,
  SUGGESTION_MIN_LENGTH: 2,
} as const;

// Type definitions
export type SearchableFields = 'all' | 'company' | 'sector' | 'location' | 'contacts';

export interface SearchOptions {
  readonly fields?: readonly SearchableFields[];
  readonly caseSensitive?: boolean;
  readonly exactMatch?: boolean;
  readonly minScore?: number;
}

export interface SearchResult<T> {
  readonly items: readonly T[];
  readonly totalCount: number;
  readonly hasMore: boolean;
}

// Input validation functions
const isValidString = (value: unknown): value is string =>
  typeof value === 'string' && value.trim().length > 0;

const isValidLead = (lead: unknown): lead is Lead =>
  typeof lead === 'object' &&
  lead !== null &&
  'metadata' in lead &&
  'processed' in lead;

/**
 * Normalize text for searching with error handling
 * Removes special characters, converts to lowercase, and normalizes whitespace
 */
export const normalizeSearchText = (text: string): string => {
  try {
    if (!isValidString(text)) {
      return '';
    }

    return text
      .toLowerCase()
      .trim()
      .replace(/[^a-z0-9\s]/gi, ' ')
      .replace(/\s+/g, ' ')
      .trim();
  } catch (error) {
    console.warn('Error normalizing search text:', error);
    return '';
  }
};

/**
 * Check if text contains the search query with robust error handling
 */
export const textContainsQuery = (
  text: string,
  query: string,
  options: {
    readonly caseSensitive?: boolean;
    readonly exactMatch?: boolean;
  } = {}
): boolean => {
  try {
    if (!isValidString(text) || !isValidString(query)) {
      return false;
    }

    const {
      caseSensitive = SEARCH_CONFIG.DEFAULT_CASE_SENSITIVE,
      exactMatch = SEARCH_CONFIG.DEFAULT_EXACT_MATCH
    } = options;

    // Normalize text unless explicitly case sensitive
    const normalizedText = caseSensitive ? text.trim() : normalizeSearchText(text);
    const normalizedQuery = caseSensitive ? query.trim() : normalizeSearchText(query);

    if (exactMatch) {
      return normalizedText === normalizedQuery;
    }

    return normalizedText.includes(normalizedQuery);
  } catch (error) {
    console.warn('Error in text contains query:', error);
    return false;
  }
};

/**
 * Extract searchable text from a lead object with error handling
 */
export const extractSearchableText = (
  lead: Lead,
  fields: readonly SearchableFields[] = SEARCH_CONFIG.DEFAULT_FIELDS
): readonly string[] => {
  try {
    if (!isValidLead(lead)) {
      return [];
    }

    const searchableTexts: string[] = [];
    const fieldsSet = new Set(fields);

    // Company information
    if (fieldsSet.has('all') || fieldsSet.has('company')) {
      const companyFields = [
        lead.metadata?.companyName,
        lead.metadata?.sector,
        lead.metadata?.companySize
      ];
      searchableTexts.push(...companyFields.filter(isValidString));
    }

    // Location information
    if (fieldsSet.has('all') || fieldsSet.has('location')) {
      const locationFields = [
        lead.metadata?.hqCity,
        lead.metadata?.hqCountry
      ];
      searchableTexts.push(...locationFields.filter(isValidString));
    }

    // Contact information
    if (fieldsSet.has('all') || fieldsSet.has('contacts')) {
      if (Array.isArray(lead.topContacts?.contactList)) {
        lead.topContacts.contactList.forEach(contact => {
          if (contact && typeof contact === 'object') {
            const contactFields = [
              contact.name,
              contact.role,
              contact.bestGuessEmail
            ];
            searchableTexts.push(...contactFields.filter(isValidString));
          }
        });
      }
    }

    // Metrics and descriptions
    if (fieldsSet.has('all')) {
      // Add metric names
      if (lead.metrics && typeof lead.metrics === 'object') {
        Object.keys(lead.metrics).forEach(metricKey => {
          if (isValidString(metricKey)) {
            searchableTexts.push(metricKey.replace(/_/g, ' '));
          }
        });
      }

      // Add overall score description
      if (lead.overallScore?.description) {
        searchableTexts.push(lead.overallScore.description);
      }
    }

    return Object.freeze(searchableTexts.filter(isValidString));
  } catch (error) {
    console.warn('Error extracting searchable text from lead:', error);
    return [];
  }
};

/**
 * Search leads based on query and options with performance optimizations
 */
export const searchLeads = (
  leads: readonly Lead[],
  query: string,
  options: SearchOptions = {}
): readonly Lead[] => {
  try {
    // Early return for empty/invalid queries
    if (!isValidString(query) || query.trim().length < SEARCH_CONFIG.MIN_QUERY_LENGTH) {
      return leads;
    }

    // Validate input array
    if (!Array.isArray(leads)) {
      console.warn('Invalid leads array provided to searchLeads');
      return [];
    }

    const {
      fields = SEARCH_CONFIG.DEFAULT_FIELDS,
      caseSensitive = SEARCH_CONFIG.DEFAULT_CASE_SENSITIVE,
      exactMatch = SEARCH_CONFIG.DEFAULT_EXACT_MATCH,
      minScore
    } = options;

    const normalizedQuery = normalizeSearchText(query);
    const queryTerms = normalizedQuery
      .split(' ')
      .filter(term => term.length > 0);

    if (queryTerms.length === 0) {
      return leads;
    }

    return Object.freeze(
      leads.filter(lead => {
        try {
          // Apply minimum score filter if specified
          if (minScore !== undefined &&
              typeof lead.processed?.averageScore === 'number' &&
              lead.processed.averageScore < minScore) {
            return false;
          }

          // Get searchable text for this lead
          const searchableTexts = extractSearchableText(lead, fields);

          if (searchableTexts.length === 0) {
            return false;
          }

          // Check if ANY query term is found in any of the searchable texts
          // This provides partial matching - searching "Defence" matches "Aerospace and Defence"
          return queryTerms.some(term =>
            searchableTexts.some(text =>
              textContainsQuery(text, term, { caseSensitive, exactMatch })
            )
          );
        } catch (error) {
          console.warn('Error filtering individual lead:', error);
          return false;
        }
      })
    );
  } catch (error) {
    console.error('Error in searchLeads function:', error);
    return [];
  }
};

/**
 * Get search suggestions based on existing leads
 */
export const getSearchSuggestions = (leads: Lead[], query: string, limit: number = 5): string[] => {
  if (!query || query.length < 2) return [];

  const suggestions = new Set<string>();
  const normalizedQuery = normalizeSearchText(query);

  leads.forEach(lead => {
    const searchableTexts = extractSearchableText(lead);

    searchableTexts.forEach(text => {
      const normalizedText = normalizeSearchText(text);

      if (normalizedText.includes(normalizedQuery) && normalizedText !== normalizedQuery) {
        // Add the original text as a suggestion
        suggestions.add(text.trim());

        // Also add individual words that match
        const words = text.split(' ').filter(word => word.length > 2);
        words.forEach(word => {
          if (normalizeSearchText(word).includes(normalizedQuery)) {
            suggestions.add(word.trim());
          }
        });
      }
    });
  });

  return Array.from(suggestions)
    .sort((a, b) => a.length - b.length) // Shorter suggestions first
    .slice(0, limit);
};

/**
 * Highlight search terms in text
 */
export const highlightSearchTerms = (
  text: string,
  query: string,
  highlightClass: string = 'search-highlight'
): string => {
  if (!query || !text) return text;

  const queryTerms = normalizeSearchText(query).split(' ').filter(term => term.length > 0);
  let highlightedText = text;

  queryTerms.forEach(term => {
    const regex = new RegExp(`(${term})`, 'gi');
    highlightedText = highlightedText.replace(
      regex,
      `<span class="${highlightClass}">$1</span>`
    );
  });

  return highlightedText;
};